import { GridFiltersView } from '../view/GridFiltersView';
import { templateGridFilters } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { GridFiltersWebComponent } from '..';
import { Filter, FilterChangeEvents, GridFiltersViewData } from '../types';
import { getColumnKeyForFilter, getFilterWithDefaultValues } from '../../utils/filters';
import { RemovableEvents } from '../../../ui/gzRemovable/types';
import { InputEvents } from '../../../ui/gzInput/types';
import { ColumnDetails, ColumnType, FilterEvents } from '../../types/common';
import { EnumListEvents, EnumListItem } from '../../../ui/gzEnumList/types';
import { TimeRangeEvents } from '../../../ui/gzDatetimeFilter/types';
import { DropdownEvents } from '../../../ui/gzDropdown/types';
import { RangeSliderEvents } from '../../../ui/gzRangeSliderInput/types';
import { ComplexSearchEvents } from '../../../ui/gzComplexSearch/types';
import { ComplexEnumListEvents } from '../../../ui/gzComplexEnumList/types';
import { CompanyFilterEvents } from '../../../ui/gzCompanyFilter/types/types';
import { GzInputSearchWebComponent } from '../../../ui/gzInput';
import { CompanyFilterDashboardDataProvider } from '../../../ui/gzSingleSelectComplex/dataProvider/company/CompanyFilterDashboardDataProvider';
import { SingleSelectComplexEvents, SingleSelectFilterType } from '../../../ui/gzSingleSelectComplex/types';

const isEqual = require('lodash.isequal');

export class GridFiltersController<
  Column extends number,
  ColumnKeys extends string
> extends BaseWebComponentController {
  public component: GridFiltersWebComponent<Column, ColumnKeys>;
  public view: GridFiltersView<Column, ColumnKeys>;
  public viewConstructor: typeof GridFiltersView;

  private static getColumnKeyFromEventTarget(target: HTMLElement): string {
    const columnKey = target.getAttribute('columnKey');

    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${target}`);
    }

    return columnKey;
  }

  constructor(component: GridFiltersWebComponent<Column, ColumnKeys>) {
    super(component);
    this.initialSetup({
      viewConstructor: GridFiltersView,
      viewTemplate: templateGridFilters,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$clearFilters,
        event: 'click',
        callback: this.clearFilters,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: RemovableEvents.SHOULD_REMOVE_ITEM,
        callback: this.removeFilter,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: InputEvents.SEARCH,
        callback: this.handleInputSearchEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: InputEvents.BLUR,
        callback: this.onSearchInputBlur,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: EnumListEvents.SELECTION_CHANGE,
        callback: this.handleEnumSelectionChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: TimeRangeEvents.TIMERANGE_CHANGE,
        callback: this.handleTimeRangeChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: TimeRangeEvents.ALL_TO_DATE,
        callback: this.handleAllToDateSelectionEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.handleSelectChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: RangeSliderEvents.CHANGE_CONFIRMED,
        callback: this.handleRangeSliderChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: ComplexSearchEvents.COMPLEX_SEARCH_CHANGED,
        callback: this.handleComplexSearchChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: ComplexEnumListEvents.COMPLEX_SELECTION_CHANGE,
        callback: this.handleComplexEnumListSelectionChangeEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: CompanyFilterEvents.VALUES_UPDATED,
        callback: this.handleCompanyValuesUpdatedEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: `${SingleSelectFilterType.COMPANY}_${SingleSelectComplexEvents.BACKEND_OPTIONS_LOADED}`,
        callback: this.handleCompanyValuesUpdatedEvent,
      },
      {
        element: this.view.dom.$filtersContainer,
        event: FilterChangeEvents.REMOTE_SELECT_CHANGED,
        callback: this.handleCompanyValuesUpdatedEventFromRemoteSelector,
      },
    ];
  }

  async getDataForView(): Promise<GridFiltersViewData<Column, ColumnKeys>> {
    return {
      filters: this.component.filters || [],
      predefinedFiltersKeys: this.component.predefinedFiltersKeys,
      columnDetailsByKey: this.getColumnDetailsByKey(),
      companyFilterDataProvider: this.component.dataProvider?.companyFilterDataProvider,
      filtersUiConfigs: this.component.filtersUiConfigs || [],
      hideResetFilters: this.component.hideResetFilters,
    };
  }

  private async onMoreFiltersUpdated(ev: WebComponentEvent) {
    const columnKeyToColumn: { [key: string]: number } = {};
    const columnToColumnDetails: { [column: number]: ColumnDetails<Column, ColumnKeys> } = {};
    for (const columnDetails of this.component.allColumnDetails) {
      columnKeyToColumn[columnDetails.key] = columnDetails.column;
      columnToColumnDetails[columnDetails.column] = columnDetails;
    }

    // Should add filters for all predefined columns & for the filters selected from "More".
    const predefinedColumns = this.component.predefinedFiltersKeys.map(columnKey => columnKeyToColumn[columnKey]);
    const selectedColumns = predefinedColumns.concat(ev.detail.selectedValues || []);

    const shouldAddFilterForColumn: { [column: number]: boolean } = {};
    for (const selectedColumn of selectedColumns) {
      shouldAddFilterForColumn[selectedColumn] = true;
    }

    // See what filters to remove and what filters to add.
    const newFilters = (this.component.filters || []).filter(existingFilter => {
      const columnKey = getColumnKeyForFilter(existingFilter) as string;
      const column = columnKeyToColumn[columnKey];
      const keepFilter = shouldAddFilterForColumn[column];
      // Filter is already added, should not add it again.
      shouldAddFilterForColumn[column] = false;
      return keepFilter;
    });

    for (const selectedColumn of selectedColumns) {
      if (shouldAddFilterForColumn[selectedColumn]) {
        const columnDetails = columnToColumnDetails[selectedColumn];
        newFilters.push(getFilterWithDefaultValues(columnDetails));
      }
    }

    await this.updateFilters(newFilters);
  }

  /**
   * Resets filters to default values.
   *
   * @param ev
   */
  private async clearFilters(ev: WebComponentEvent) {
    const columnDetailsByKey = this.getColumnDetailsByKey();
    const defaultFilters: Array<Filter<ColumnKeys>> = [];
    for (const filter of this.component.filters || []) {
      const columnKey = getColumnKeyForFilter<ColumnKeys>(filter);
      const columnDetails = columnDetailsByKey[columnKey];
      defaultFilters.push(getFilterWithDefaultValues(columnDetails));
    }

    await this.updateFilters(defaultFilters);
  }

  /**
   * Resets filters that are dependent on a different filter
   */
  private updateFiltersBasedOnDependencyMapping(mapping, mainColumnKey, filters) {
    const columnDetailsByKey = this.getColumnDetailsByKey();
    const newFilters: Array<Filter<ColumnKeys>> = filters.map(filter => {
      const columnKey = getColumnKeyForFilter<ColumnKeys>(filter);
      if (!mapping[mainColumnKey].includes(columnKey)) {
        return filter;
      }
      const columnDetails = columnDetailsByKey[columnKey];
      return getFilterWithDefaultValues(columnDetails);
    });

    return newFilters;
  }

  private async clearOneFilter(filterToResetKey: ColumnKeys) {
    const columnDetailsByKey = this.getColumnDetailsByKey();

    const newFilters: Array<Filter<ColumnKeys>> = [];

    for (const filter of this.component.filters || []) {
      const columnKey = getColumnKeyForFilter<ColumnKeys>(filter);
      if (filterToResetKey === columnKey) {
        const columnDetails = columnDetailsByKey[columnKey];
        newFilters.push(getFilterWithDefaultValues(columnDetails));
      } else {
        newFilters.push(filter);
      }
    }

    const shouldMakeChangesVisible = false;
    await this.updateFilters(newFilters, shouldMakeChangesVisible);
  }

  private async removeFilter(ev: WebComponentEvent) {
    const removedFilterKey = ev.target.getAttribute('columnKey');
    const newFilters = this.component.filters.filter(filter => getColumnKeyForFilter(filter) !== removedFilterKey);

    await this.updateFilters(newFilters);
  }

  public async addFilter(filterValue: Filter<ColumnKeys>): Promise<void> {
    const newFilters = this.component.filters || [];
    const columnKey = getColumnKeyForFilter(filterValue);
    const existingFilterIndex = newFilters.findIndex(filter => getColumnKeyForFilter(filter) === columnKey);
    if (existingFilterIndex > -1) {
      newFilters[existingFilterIndex] = filterValue;
    } else {
      newFilters.push(filterValue);
    }

    await this.updateFilters(newFilters);
  }

  private async updateFilters(newFilters: Array<Filter<ColumnKeys>>, shouldMakeChangesVisible = true) {
    this.setFilters(newFilters, shouldMakeChangesVisible);
    if (shouldMakeChangesVisible) {
      await this.render();
    }
  }

  private onSearchInputBlur(ev: WebComponentEvent): void {
    const filter = ev.target as GzInputSearchWebComponent;
    const columnKey = ev.target.getAttribute('columnKey');
    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${ev.target}`);
    }

    const filterObj: Filter<ColumnKeys> | undefined = this.component.filters.find(obj => columnKey in obj);

    if (filterObj) {
      const filterValue =
        filterObj[columnKey].regex?.pattern || filterObj[columnKey].startsWith || filterObj[columnKey].contains;
      filter.setFormValue(filterValue);
      filter.requestUpdate();
    }
  }

  private handleInputSearchEvent(ev: WebComponentEvent) {
    const columnKey = ev.target.getAttribute('columnKey');

    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${ev.target}`);
    }

    const value = ev.detail.sourceEvent;

    let filterValue;
    if (value) {
      if (typeof value === 'number') {
        filterValue = { eq: value };
      } else {
        if (value.includes('*')) {
          filterValue = { regex: { pattern: value } };
        } else {
          filterValue = { startsWith: value };
        }
      }
    } else {
      filterValue = this.getDefaultFilterValuesFromKey(columnKey);
    }

    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private handleRangeSliderChangeEvent(ev: WebComponentEvent) {
    const columnKey = ev.target.getAttribute('columnKey');

    // TODO: when add from "more", error is thrown ev.target does not exists
    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${ev.target}`);
    }

    let filterValue;
    const value = ev.detail.sourceEvent;
    if (value) {
      filterValue = {
        range: value,
      };
    } else {
      filterValue = this.getDefaultFilterValuesFromKey(columnKey);
    }

    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private handleComplexSearchChangeEvent(ev: WebComponentEvent) {
    const columnKey = ev.target.getAttribute('columnKey');

    // TODO: when add from "more", error is thrown ev.target does not exists
    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${ev.target}`);
    }

    let filterValue;
    const searchKey = ev.detail.searchKey;
    const searchValue = ev.detail.searchValue;
    if (searchKey || searchValue) {
      filterValue = {
        complexSearch: {
          field: searchKey,
          value: searchValue,
        },
      };
    } else {
      filterValue = this.getDefaultFilterValuesFromKey(columnKey);
    }

    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private handleComplexEnumListSelectionChangeEvent(ev: WebComponentEvent) {
    const columnKey = ev.target.getAttribute('columnKey');

    // TODO: when add from "more", error is thrown ev.target does not exists
    if (!columnKey) {
      throw new Error(`Missing columnKey attribute from target ${ev.target}`);
    }

    let filterValue;
    const selectedValues = ev.detail.selectedValues;
    if (selectedValues) {
      filterValue = {
        complexEnumFilter: ev.detail.selectedValues,
      };
    } else {
      filterValue = this.getDefaultFilterValuesFromKey(columnKey);
    }

    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private async handleCompaniesChangeEvent(selectedValues: Array<string>, selectedLabels: Array<string>) {
    if (!this.view.dom.$tagsFilter) {
      return;
    }

    const selectedFilterOptions: Array<EnumListItem> = [];

    for (let index = 0; index < selectedValues.length; index++) {
      selectedFilterOptions.push({
        selected: true,
        label: selectedLabels[index],
        value: selectedValues[index],
      });
    }

    const companyFilter = this.component.filters.filter(filter => filter.hasOwnProperty('company')).pop();

    if (!companyFilter) {
      return;
    }

    // setting empty array when the company filter is cleaned in back-end (following company deletion / lost access)
    const companyFilterOptions =
      companyFilter['company'].in || (companyFilter['company'].equals ? [companyFilter['company'].equals] : []);

    if (isEqual(companyFilterOptions.sort(), selectedValues.sort())) {
      return;
    }

    await this.clearOneFilter('tags' as ColumnKeys);
    await this.view.dom.$tagsFilter.setSelectedItems([]);
    this.view.updateTagsFilterWithSelectedCompanies(selectedFilterOptions);
  }

  private async handleCompanyValuesUpdatedEventFromRemoteSelector(ev: WebComponentEvent): Promise<void> {
    if (!ev.detail.selectedValue || !this.view.dom.$tagsFilter) {
      return;
    }
    await this.clearOneFilter('tags' as ColumnKeys);
    await this.view.dom.$tagsFilter.setSelectedItems([]);

    if (!ev.detail.label) {
      ev.detail.label = await CompanyFilterDashboardDataProvider.getCompanyNameById(ev.detail.selectedValue);
    }

    await this.view.updateTagsFilterWithRemoteSelectorOption(ev.detail as { label: string; selectedValue: string });
  }

  private handleCompanyValuesUpdatedEvent(ev: WebComponentEvent) {
    const selectedOptions = (ev.detail.filterOptions as Array<EnumListItem>).filter(option => option.selected);
    this.view.updateTagsFilterWithSelectedCompanies(selectedOptions);
  }

  private getDefaultFilterValuesFromKey(filterKey: string) {
    const columnDetails = this.component.allColumnDetails
      .filter(column => column.key === filterKey)
      .pop() as ColumnDetails<Column, ColumnKeys>;

    if (!columnDetails) {
      throw new Error(`Missing column details for column key: ${filterKey}`);
    }

    return getFilterWithDefaultValues<Column, ColumnKeys>(columnDetails)[filterKey];
  }

  public setFilters(newFilters: Array<Filter<ColumnKeys>>, shouldMakeChangesVisible = true) {
    this.component.filters = newFilters;

    if (!shouldMakeChangesVisible) {
      return;
    }

    // If smart views are enabled, we persist the filters when the Save button is pressed
    if (!this.component.smartViewsEnabled) {
      // Set filters in the background but don't wait for the result. If the call fails,
      // it's fine - filters won't be persisted. But this is better than waiting and
      // showing a loader, since most of the time this call will succeed.
      this.component.dataProvider
        .setFilters({ filters: newFilters, viewId: this.component.viewId })
        .catch(console.error);
    }

    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(FilterEvents.FILTERS_UPDATED, {
        detail: {
          updatedFilters: newFilters,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private async handleEnumSelectionChangeEvent(ev: WebComponentEvent) {
    if (ev.target.getAttribute('id') === GridFiltersView.MORE_FILTERS_ID) {
      this.onMoreFiltersUpdated(ev);
    } else {
      this.handleFilterSelectionChangeEvent(ev);
    }
  }

  private async handleFilterSelectionChangeEvent(ev: WebComponentEvent) {
    const columnKey = GridFiltersController.getColumnKeyFromEventTarget(ev.target);
    const filterValue = await this.getEnumFilterChangeValue(ev, columnKey);

    let filters = this.getUpdatedFilterValues(columnKey, filterValue);

    if (
      this.component.filterDependencyMapping &&
      Object.keys(this.component.filterDependencyMapping).includes(columnKey)
    ) {
      filters = this.updateFiltersBasedOnDependencyMapping(this.component.filterDependencyMapping, columnKey, filters);
    }

    this.updateFilters(filters);
  }

  private async getEnumFilterChangeValue(ev: WebComponentEvent, columnKey: string) {
    const columnDetailsByKey = this.getColumnDetailsByKey();
    const columnDetails = columnDetailsByKey[columnKey];

    switch (columnDetails.type) {
      case ColumnType.BACKEND_SINGLE_SELECT:
        return this.handleSelectFilterChangeSideEvents(ev, columnKey);
      case ColumnType.REMOTE_SELECT:
        return this.handleRemoteSelectFilterChangeSideEvents(ev, columnKey);
      case ColumnType.COMPLIANCE_SELECT:
          return this.handleComplianceSelectFilterChangeSideEvents(ev, columnKey);
      case ColumnType.ENUM:
      default:
        return await this.handleEnumFilterChangeSideEvents(ev, columnKey);
    }
  }

  private async handleEnumFilterChangeSideEvents(ev: WebComponentEvent, columnKey: string) {
    const selectedValues = ev.detail.selectedValues;
    const selectedLabels = ev.detail.selectedLabels;

    if (columnKey === 'company') {
      await this.handleCompaniesChangeEvent(selectedValues, selectedLabels);
    }

    return selectedValues.length ? { in: selectedValues } : this.getDefaultFilterValuesFromKey(columnKey);
  }

  private async handleRemoteSelectFilterChangeSideEvents(ev: WebComponentEvent, columnKey: string) {
    if (columnKey === 'companyId') {
      this.handleCompanyValuesUpdatedEventFromRemoteSelector(ev);
    }

    return { exactMatch: ev.detail.selectedValue };
  }

  private async handleComplianceSelectFilterChangeSideEvents(ev: WebComponentEvent, columnKey: string) {
    const selectedValue = ev.detail.selectedValue;

    return selectedValue ? { value: selectedValue } : this.getDefaultFilterValuesFromKey(columnKey);
  }

  private async handleSelectFilterChangeSideEvents(ev: WebComponentEvent, columnKey: string) {
    const selectedValue = ev.detail.selectedValue;
    const selectedLabel = ev.detail.selectedLabel;

    if (columnKey === 'company') {
      await this.handleCompaniesChangeEvent(selectedValue ? [selectedValue] : [], selectedLabel ? [selectedLabel] : []);
    }
    return selectedValue ? { equals: selectedValue } : this.getDefaultFilterValuesFromKey(columnKey);
  }

  private handleTimeRangeChangeEvent(ev: WebComponentEvent) {
    const { timeRange } = ev.detail;
    const columnKey = GridFiltersController.getColumnKeyFromEventTarget(ev.target);

    const filterValue = timeRange ? { timeRange } : this.getDefaultFilterValuesFromKey(columnKey);
    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private handleAllToDateSelectionEvent(ev) {
    const columnKey = GridFiltersController.getColumnKeyFromEventTarget(ev.target);

    const filterValue = ev.detail;
    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }

  private getUpdatedFilterValues(columnKey: string, filterValue: any): Array<Filter<ColumnKeys>> {
    const filters = this.component.filters;
    const hasFilterForColumnKey = !!filters.filter(filter => getColumnKeyForFilter(filter) === columnKey).length;

    if (!hasFilterForColumnKey) {
      throw new Error(`Unexpected event from filter '${columnKey}' - component missing.`);
    }

    for (const filter of filters) {
      if (filter.hasOwnProperty(columnKey)) {
        filter[columnKey] = filterValue;
      }
    }

    return filters;
  }

  private getColumnDetailsByKey(): { [key: string]: ColumnDetails<Column, ColumnKeys> } {
    const columnDetailsByKey: { [key: string]: ColumnDetails<Column, ColumnKeys> } = {};
    for (const columnDetails of this.component.allColumnDetails) {
      columnDetailsByKey[columnDetails.key] = columnDetails;
    }

    return columnDetailsByKey;
  }

  private handleSelectChangeEvent(ev) {
    const { newValue, oldValue } = ev.detail;
    if (newValue === null && oldValue === null) {
      return;
    }
    const columnKey = GridFiltersController.getColumnKeyFromEventTarget(ev.target);

    const filterValue = newValue || { value: null };
    const filters = this.getUpdatedFilterValues(columnKey, filterValue);

    this.setFilters(filters);
  }
}
